import React from "react";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { makeStyles } from "@material-ui/styles";

import InfoIcon from "@material-ui/icons/Info";

import { Paper, Typography } from "@material-ui/core";

import { translate } from "jazasoft";

import Table from "../../../components/Table";
import MuiTable from "../../../components/MuiTable";
import ResourceKeys from "../../../utils/ResourceKeys";
import { useDeepEffect, useDeepMemo, useQuery, useSafeState, useSetting, useTime } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { getDistinctValues, isEmpty, isTrue } from "../../../utils/helpers";
import { format } from "../../../utils/dateUtils";
import { getSortedSizeKeyList } from "../../../helpers/orderHelper";

import OrderActivityDetailsDrawer from "./OrderActivityDetailsDrawer";

const NON_SB_FIELDS = [
  "id",
  "orderQty",
  "label",
  "product",
  "color",
  "flowRef",
  "customerFlowRef",
  "inseam",
  "destination",
  "delivery",
  "total",
  "info"
];

const view = "order";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "#fafafa",
    flex: 1
  },

  accordionDetail: {
    display: "flex",
    flexDirection: "column",
    padding: 0
  },
  padding: {
    padding: "1em"
  }
}));

const oColumns = [
  { dataKey: "id", title: "Id" },
  { dataKey: "poRef", title: "PO Ref." },
  { dataKey: "customerPoRef", title: "Customer PO Ref" },
  { dataKey: "customerName", title: "Customer" },
  { dataKey: "seasonName", title: "Season" },
  { dataKey: "desc", title: "Description" },
  { dataKey: "customField1", title: "Custom Field 1" },
  { dataKey: "customField2", title: "Custom Field 2" },
  { dataKey: "customField3", title: "Custom Field 3" }
];

const flowInfoColumns = [
  { dataKey: "id", title: "Id" },
  { dataKey: "flowRef", title: "FlowRef." },
  { dataKey: "customerFlowRef", title: "Customer FLowRef" },
  { dataKey: "productName", title: "Product" },
  { dataKey: "ratio", title: "Ratio" },
  { dataKey: "style", title: "Style" },
  { dataKey: "styleNo", title: "Style No" },
  { dataKey: "fit", title: "Fit" },
  { dataKey: "color", title: "Color" },
  { dataKey: "inseam", title: "Inseam" },
  { dataKey: "destination", title: "Destination" },
  { dataKey: "deliveryDate", title: "Delivery Date" },
  { dataKey: "delMode", title: "Del Mode" },
  { dataKey: "orderQty", title: "Order Qty" },
  { dataKey: "extra", title: "Extra" },
  { dataKey: "customField1", title: "Custom Field 1" },
  { dataKey: "customField2", title: "Custom Field 2" },
  { dataKey: "customField3", title: "Custom Field 3" }
  // { dataKey: "serialNo", title: "Serial No" },
  // { dataKey: "parentId", title: "Parent Id" },
  // { dataKey: "unitIds", title: "Unit Ids" },
  // { dataKey: "parentColor", title: "Parent Color" },
  // { dataKey: "exFactoryDate", title: "Ex Factory Date" },
  // { dataKey: "styleImage", title: "Style Image" },
];

const partColumns = [
  { dataKey: "id", title: "Id" },
  // { dataKey: "serial", title: "Serial" },
  { dataKey: "partName", title: "Part" },
  { dataKey: "placement", title: "Placement" },
  { dataKey: "bomCu", title: "BOM Cu" },
  { dataKey: "uom", title: "UOM" },
  { dataKey: "bomFabColor", title: "BOM Fab Color" },
  { dataKey: "fabOrderQty", title: "Fab Order Qty" },
  { dataKey: "bomItemCode", title: "BOM Item Code" }
];

const OrderDetails = ({ userActivity, translate }) => {
  const classes = useStyles();

  const { utc_offset } = useTime();

  const prevDataObject = JSON.parse(userActivity?.prevValue || "{}");
  const currDataObject = JSON.parse(userActivity?.currValue || "{}");
  const order = JSON.parse(userActivity?.resBody || "{}");

  const [selectedKey, setSelectedKey] = useSafeState(null);
  const [drawerState, setDrawerState] = useSafeState({ open: false, prevObject: null, currObject: null, sbList: null, resource: null });

  const resource = ResourceKeys[userActivity?.entityName];

  const { data: childActivityList = [] } = useQuery({
    url: "system-activities",
    params: { search: `parentId==${userActivity?.id}` }
  });

  const flowInfoRows = useDeepMemo(() => {
    const flowInfoList = JSON.parse(userActivity?.resBody || "{}")?.flowInfoList;

    if (userActivity?.method === "DELETE" || userActivity?.method === "POST") {
      return flowInfoList.map(fi => ({ ...fi, operation: userActivity?.method === "POST" ? "write" : "delete" }));
    }

    return flowInfoList.map(fi => {
      const activity = childActivityList.find(a => a.entityId === fi.id);
      if (!isEmpty(activity)) {
        const prev = JSON.parse(activity?.prevValue || "{}");
        const curr = JSON.parse(activity?.currValue || "{}");
        return {
          ...fi,
          prevObject: prev,
          currObject: curr,
          operation: "update"
        };
      }

      return { ...fi, prevObject: fi, currObject: fi, operation: "write" };
    });
  }, [childActivityList, userActivity]);

  //** To initialize Selection of flowInfo **//
  useDeepEffect(() => {
    if (isEmpty(flowInfoRows)) return;
    setSelectedKey(flowInfoRows[0]?.id);
  }, [flowInfoRows]);

  //TODO: change the logic when all settings are updated.
  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, resource);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, "flowInfo");

  const orderColumns = useDeepMemo(() => {
    if (userActivity?.entityName !== "Order" || isEmpty(orderFields)) return [];

    let columns = oColumns.map(col => {
      return {
        dataKey: col.dataKey,
        title: translate(`resources.${resource}.fields.${col.dataKey}`, { _: col.title })
      };
    });

    columns = columns.filter(col => orderFields.includes(col.dataKey));
    return columns;
  }, [orderFields, resource, prevDataObject, currDataObject, userActivity, oColumns]);

  const flowColumns = useDeepMemo(() => {
    if (isEmpty(flowInfoFields)) return [];

    let columns = flowInfoColumns.map(col => {
      return {
        dataKey: col.dataKey,
        title: translate(`resources.${resource}.fields.${col.dataKey}`, { _: col.title })
      };
    });

    columns = columns.filter(col => flowInfoFields.includes(col.dataKey));

    return columns;
  }, [flowInfoFields, flowInfoColumns]);

  const partRows = useDeepMemo(() => {
    if (isEmpty(selectedKey)) return [];

    const flowInfo = order.flowInfoList?.find(fi => fi.id === selectedKey);
    const partList =
      isEmpty(flowInfo.partList) && !isTrue(flowInfo.isLeaf)
        ? order.flowInfoList.filter(fi => fi.parentId === flowInfo.id)?.flatMap(fi => fi.partList)
        : flowInfo.partList;

    const rows = partList.map(part => {
      const partActivity = childActivityList?.find(a => a.entityName === "Part" && a.entityId === part.id);
      return {
        ...part,
        prevObject: !isEmpty(partActivity) ? JSON.parse(partActivity.prevValue || "{}") : { ...part },
        currObject: !isEmpty(partActivity) ? JSON.parse(partActivity.currValue || "{}") : { ...part },
        operation: !isEmpty(partActivity) ? partActivity.operation : null
      };
    });

    return rows;
  }, [selectedKey, childActivityList, order]);

  const selectedRow = useDeepMemo(() => {
    if (isEmpty(selectedKey) || isEmpty(flowInfoRows)) return {};
    return flowInfoRows.find(r => selectedKey === r.id);
  }, [selectedKey, flowInfoRows]);

  const onClick = record => {
    if (!isEmpty(record)) {
      setDrawerState({ open: true, sbList: record.sbList.filter(sb => sb.operation === "update"), resource: "SizeBreakup" });
    }
  };

  //** Prepare Size Breakup Columns for View mode **/
  const { columns, headerRows } = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    let flowInfoList = [];
    let sizeBreakupList = [];
    let hasInseam = false;
    if (view === "order") {
      let children = order?.flowInfoList?.filter(fi => isEmpty(fi.orderParentId)).filter(fi => fi.parentId === selectedRow?.id) || [];
      children = children.flatMap(child => {
        const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
        return isEmpty(childList) ? [child] : childList;
      });

      flowInfoList = isEmpty(children) ? [selectedRow] : children;
      hasInseam = flowInfoList.some(c => !isEmpty(c.inseam));
      sizeBreakupList = flowInfoList
        .flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") || [])
        .map(sb => (hasInseam ? { ...sb, sizeGroup: "G1", sizeKey: `G1_${sb.size}` } : sb));
    } else {
      flowInfoList = selectedRow?.flowInfoList || [];
      hasInseam = flowInfoList.some(c => !isEmpty(c.inseam));
      sizeBreakupList = flowInfoList
        .flatMap(fi => fi.fpoSizeBreakupList || [])
        .map(sb => (hasInseam ? { ...sb, sizeGroup: "G1", sizeKey: `G1_${sb.size}` } : sb));
    }

    const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sbList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);

    let columns = [];
    if (flowInfoList?.length > 1) {
      let cols = [
        { dataKey: "product", title: "Product", align: "center" },
        { dataKey: "color", title: "Color", align: "center" },
        { dataKey: "flowRef", title: "Flow Ref", align: "center" },
        { dataKey: "customerFlowRef", title: "Customer Flow Ref", align: "center" },
        { dataKey: "inseam", title: "Inseam", align: "center" },
        { dataKey: "destination", title: "Destination", align: "center" },
        { dataKey: "delivery", title: "Delivery Date", align: "center" }
      ];
      const productCount = getDistinctValues(flowInfoList.map(c => c.product?.id))?.length;
      const colorCount = getDistinctValues(flowInfoList.map(c => c.color))?.length;
      const flowRefCount = getDistinctValues(flowInfoList.map(c => c.flowRef))?.length;
      const cFlowRefCount = getDistinctValues(flowInfoList.map(c => c.customerFlowRef))?.length;
      const inseamCount = getDistinctValues(flowInfoList.map(c => c.inseam))?.length;
      const destCount = getDistinctValues(flowInfoList.map(c => c.destination))?.length;
      const deliveryCount = getDistinctValues(flowInfoList.map(c => c.delivery))?.length;

      if (productCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "product");
      }
      if (colorCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "color");
      }
      if (flowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "flowRef");
      }
      if (cFlowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "customerFlowRef");
      }
      if (inseamCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "inseam");
      }
      if (destCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "destination");
      }
      if (deliveryCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "delivery");
      }
      columns = [...cols];
    }

    const sizeKeys = getSortedSizeKeyList(sbList);
    let groupRow = {};
    let sizeList = [];
    let sizeGroup;
    for (let i = 0; i < sizeKeys.length; i++) {
      const sizeKey = sizeKeys[i];
      const sb = sbList.find(sb => sb.sizeKey === sizeKey);
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sizeKey] = sb.sizeGroup;
      }
      sizeList.push({
        dataKey: sizeKey,
        title: sizeKey.split("_")[1],
        element: ({ record, column }) => <span>{record[column.dataKey] || "-"}</span>,
        leftBorder
      });
    }

    const haveSizeGroup = !hasInseam && getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 0;

    columns = [
      ...columns,
      ...sizeList,
      { dataKey: "total", title: "Total", leftBorder: true },
      {
        dataKey: "info",
        title: "Info",
        element: ({ record }) =>
          !record.isTotalRow ? (
            <div onClick={() => onClick && onClick(record)}>
              <InfoIcon />
            </div>
          ) : (
            <></>
          )
      }
    ];
    columns = columns.map(c => ({
      ...c,
      align: "center",
      colSpan: ({ column, row, rowIdx }) =>
        !haveSizeGroup || rowIdx === 1 || NON_SB_FIELDS.includes(c.dataKey)
          ? 1
          : isEmpty(row[column.dataKey])
          ? 0
          : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 0) {
      headerRows = [
        groupRow,
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    } else {
      headerRows = [
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    }

    return { columns, headerRows };
  }, [selectedRow, order]);

  //** Prepare Size Breakup Rows for View mode **/
  const rows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList)) return [];

    let flowInfoList = [];
    if (view === "order") {
      let children =
        order?.flowInfoList
          ?.filter(fi => isEmpty(fi.orderParentId))
          .filter(fi => fi.parentId === selectedRow?.id)
          .sort((a, b) => a.serialNo - b.serialNo) || [];
      children = children.flatMap(child => {
        const childList = order?.flowInfoList?.filter(fi => fi.parentId === child?.id) || [];
        return isEmpty(childList) ? [child] : childList;
      });

      flowInfoList = isEmpty(children) ? [selectedRow] : children;
      flowInfoList = flowInfoList
        .sort((a, b) => a.serialNo - b.serialNo)
        .map(fi => ({ ...fi, sbList: fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") }));
    } else {
      flowInfoList = selectedRow?.flowInfoList || [];
      flowInfoList = flowInfoList
        .map(fi => ({ ...fi, colorId: `${fi.product?.id}-${fi.style}-${fi.color}`, sbList: fi.fpoSizeBreakupList }))
        .sort((a, b) => (a.colorId !== b.colorId ? (a.colorId < b.colorId ? -1 : 1) : a.serialNo - b.serialNo));
    }
    const hasInseam = flowInfoList.some(c => !isEmpty(c.inseam));

    let rows = flowInfoList.map(fi => ({
      id: fi.id,
      product: fi.product?.name,
      color: fi.color,
      flowRef: fi.flowRef,
      customerFlowRef: fi.customerFlowRef,
      inseam: fi.inseam,
      destination: fi.destination,
      delivery: !isEmpty(utc_offset) && !isEmpty(fi.exFactoryDate) ? `${format(fi.exFactoryDate, utc_offset, "dd MMM, yy")}` : "",
      total: fi.sbList?.reduce((acc, sb) => acc + sb.qty, 0),
      orderQty: fi.orderQty,
      sbList: fi.sbList?.map(sb => {
        const sbActivity = childActivityList.find(a => a.entityName === "SizeBreakup" && a.entityId === sb.id);
        let prev = sb;
        let curr = sb;
        if (!isEmpty(sbActivity)) {
          prev = JSON.parse(sbActivity.prevValue || "{}");
          curr = JSON.parse(sbActivity.currValue || "{}");
        }
        return hasInseam
          ? { ...sb, sizeKey: `G1_${sb.size}`, currObject: curr, prevObject: prev, operation: !isEmpty(sbActivity) ? sbActivity.operation : null }
          : { ...sb, currObject: curr, prevObject: prev, operation: !isEmpty(sbActivity) ? sbActivity.operation : null };
      }),
      ...fi.sbList?.map(sb => (hasInseam ? { ...sb, sizeKey: `G1_${sb.size}` } : sb)).reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb.qty }), {})
    }));
    if (rows.every(row => row.total === 0)) {
      rows = [];
    }
    if (rows?.length > 1 && !isEmpty(flowInfoList)) {
      const sizeKeys = flowInfoList[0].sbList?.map(sb => (hasInseam ? `G1_${sb.size}` : sb.sizeKey)) || [];
      const totalRow = [...sizeKeys, "total"].reduce((acc, key) => ({ ...acc, [key]: rows.reduce((acc, row) => acc + row[key], 0) }), {});
      const cols = columns.filter(c => ![...sizeKeys, "total"].includes(c.dataKey));
      if (!isEmpty(cols)) {
        rows.push({ ...totalRow, [cols[cols.length - 1].dataKey]: "Total", isTotalRow: true });
      }
    }
    return rows;
  }, [selectedRow, order, columns]);

  const onInlineActionClick = (prevOrder, currOrder, resource) => {
    if (!isEmpty(prevOrder) && !isEmpty(currOrder)) {
      setDrawerState({ open: true, prevObject: prevOrder, currObject: currOrder, resource: resource });
    }
  };

  const onFlowInlineActionClick = (event, action, row) => {
    if (!isEmpty(row.prevObject) && !isEmpty(row.currObject)) {
      setDrawerState({ open: true, prevObject: row.prevObject, currObject: row.currObject, resource: "FlowInfo" });
    }
  };

  const onPartInlineActionClick = (event, action, row) => {
    if (!isEmpty(row.prevObject) && !isEmpty(row.currObject)) {
      setDrawerState({ open: true, prevObject: row.prevObject, currObject: row.currObject, resource: "Part" });
    }
  };

  const onRowClick = row => {
    setSelectedKey(row.id);
  };

  return (
    <PerfectScrollbar className={classes.root}>
      <OrderActivityDetailsDrawer
        open={drawerState?.open || false}
        prevObject={drawerState?.prevObject || {}}
        currObject={drawerState?.currObject || {}}
        sbList={drawerState?.sbList || []}
        resource={drawerState?.resource}
        onDrawerClose={() => setDrawerState({ open: false, prevObject: null, currObject: null, resource: null })}
      />

      <div style={{ padding: 16 }}>
        <Typography style={{ marginBottom: 12, color: "#666666" }} variant="h6">
          Order Summary
        </Typography>
        <Paper>
          {
            <MuiTable
              columns={orderColumns}
              rows={
                userActivity.operation === "write" || userActivity?.operation === "update"
                  ? [
                      {
                        ...currDataObject,
                        ...orderColumns.reduce(
                          (acc, col) => ({
                            ...acc,
                            [col.dataKey]: !isEmpty(currDataObject[col.dataKey]) ? currDataObject[col.dataKey] : "-"
                          }),
                          {}
                        )
                      }
                    ]
                  : [
                      {
                        ...prevDataObject,
                        ...orderColumns.reduce(
                          (acc, col) => ({
                            ...acc,
                            [col.dataKey]: !isEmpty(prevDataObject[col.dataKey]) ? prevDataObject[col.dataKey] : "-"
                          }),
                          {}
                        )
                      }
                    ]
              }
              inlineActions={userActivity?.operation === "update" ? [{ name: "info", tooltip: "Info", icon: <InfoIcon />, align: "center" }] : []}
              onInlineActionClick={() => onInlineActionClick(prevDataObject, currDataObject, "Order")}
            />
          }
        </Paper>
      </div>

      <div style={{ padding: 16 }}>
        <Typography style={{ marginBottom: 8, color: "#666666" }} variant="h6">
          Order Details
        </Typography>
        <div>
          {!isEmpty(flowInfoRows) && (
            <MuiTable
              columns={flowColumns}
              rows={flowInfoRows}
              defaultExpanded
              onRowClick={onRowClick}
              rowStyle={({ row }) => (selectedKey === row?.id ? { backgroundColor: "#BFDDF8" } : {})}
              inlineActions={row => (row?.operation === "update" ? [{ name: "info", tooltip: "Info", icon: <InfoIcon />, align: "center" }] : [])}
              onInlineActionClick={onFlowInlineActionClick}
            />
          )}
        </div>
      </div>

      {!isEmpty(partRows) && (
        <div style={{ padding: 16 }}>
          <Typography style={{ marginBottom: 8, color: "#666666" }} variant="h6">
            Part Details
          </Typography>
          <div>
            {!isEmpty(partRows) && (
              <MuiTable
                columns={partColumns}
                rows={partRows}
                inlineActions={row => (row?.operation === "update" ? [{ name: "info", tooltip: "Info", icon: <InfoIcon />, align: "center" }] : [])}
                onInlineActionClick={onPartInlineActionClick}
              />
            )}
          </div>
        </div>
      )}

      {!isEmpty(rows) && (
        <div style={{ padding: 16 }}>
          <Typography style={{ marginBottom: 8, color: "#666666" }} variant="h6">
            Size Breakup Details
          </Typography>
          <div>
            {!isEmpty(rows) && (
              <Table
                classes={{ table: classes.nowrapTable }}
                columns={columns}
                headerRows={headerRows}
                rows={rows}
                i18nKey="resources.orders.fields.flowInfo"
                headerCellStyle={column => ({
                  width: NON_SB_FIELDS.includes(column?.dataKey) ? 150 : undefined,
                  borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                  fontWeight: "bold",
                  backgroundColor: "#C6D0DA"
                })}
                cellStyle={({ column, record }) => ({
                  fontWeight: record.isTotalRow ? "bold" : "normal",
                  borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                })}
              />
            )}
          </div>
        </div>
      )}
    </PerfectScrollbar>
  );
};

export default translate(OrderDetails);
